import { useEffect, useState } from "react";
import { DownloadSimple, FileXls, FileCsv, FloppyDisk, Buildings, UsersThree, CaretDown, ChartBar, UsersFour, Wheelchair, UserPlus, HandHeart, IdentificationCard, ListChecks, CheckCircle, ArrowRight, UploadSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, useApi, download } from "../lib/api";
import { Loader, Empty, Field, StatCard, ReportSection, DataTable } from "../components/kit";

const CATS = ["General", "SC", "ST", "OBC"];
const CSV_TYPES = [["students", "Student details (SDMS)"], ["enrolment", "Class-wise enrolment"], ["category", "Enrolment by category"],
  ["minority", "Minority students"], ["age", "Enrolment by age"], ["admissions", "Admissions & repeaters"], ["cwsn", "CWSN details"],
  ["teacher_summary", "Teachers summary"], ["staff", "Teachers & staff list"], ["profile", "School & infrastructure"], ["readiness", "Readiness checklist"]];
const YESNO = new Set(["drinking_water", "electricity", "library", "playground", "internet", "ramps", "medical_checkup", "midday_meal_kitchen", "solar_panel"]);
const NUMS = new Set(["classrooms", "other_rooms", "toilets_boys", "toilets_girls", "cwsn_toilets", "library_books", "computers", "establishment_year"]);
const INFRA = [
  ["udise_code", "UDISE code (11 digits)"], ["management", "Management"], ["school_category", "School category"], ["school_type", "School type"],
  ["medium", "Medium of instruction"], ["affiliation_board", "Affiliation board"], ["establishment_year", "Year of establishment"],
  ["building_status", "Building status"], ["classrooms", "Classrooms"], ["other_rooms", "Other rooms"], ["toilets_boys", "Toilets (boys)"],
  ["toilets_girls", "Toilets (girls)"], ["cwsn_toilets", "CWSN-friendly toilets"], ["drinking_water", "Drinking water"], ["electricity", "Electricity"],
  ["library", "Library"], ["library_books", "Library books"], ["playground", "Playground"], ["computers", "Computers"], ["internet", "Internet"],
  ["ramps", "Ramps"], ["boundary_wall", "Boundary wall"], ["medical_checkup", "Medical check-up"], ["midday_meal_kitchen", "Mid-day meal kitchen"],
  ["solar_panel", "Solar panel"], ["pin_code", "PIN code"], ["block", "Block"], ["district", "District"], ["state", "State"],
];
const INFRA_SUMMARY = [["classrooms", "Classrooms"], ["toilets_boys", "Boys toilets"], ["toilets_girls", "Girls toilets"], ["drinking_water", "Drinking water"],
  ["electricity", "Electricity"], ["library", "Library"], ["computers", "Computers"], ["playground", "Playground"]];

function qs(f) {
  return new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
}

const bgRow = (label, x) => [label, x.boys, x.girls, x.transgender, x.total];

function useExporter(f, ay) {
  return async (format, type) => {
    try {
      await download(`/udise/export?${qs({ ...f, format, type })}`, `UDISE_${ay || ""}_${type === "all" ? "workbook" : type}.${format}`);
      toast.success(`${format === "xlsx" ? "Excel" : "CSV"} downloaded`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
}

const SectionExport = ({ type, exp }) => (
  <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1">
    <button className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-emerald-300 transition-colors duration-200 hover:bg-emerald-500/15"
      onClick={() => exp("xlsx", type)} data-testid={`udise-section-${type}-xlsx`}><FileXls size={14} weight="bold" /> Excel</button>
    <button className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors duration-200 hover:bg-white/10"
      onClick={() => exp("csv", type)} data-testid={`udise-section-${type}-csv`}><FileCsv size={14} weight="bold" /> CSV</button>
  </div>
);

function Infrastructure({ onSaved }) {
  const { data, loading } = useApi("/udise/profile");
  const [f, setF] = useState({});
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (data) setF(data);
  }, [data]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/udise/profile", { data: f });
      toast.success("UDISE profile saved");
      onSaved();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <div className="mt-6 rounded-2xl border border-white/[0.07] bg-black/20">
      <button className="flex w-full items-center gap-3 px-5 py-4 text-left" onClick={() => setOpen(!open)} data-testid="udise-infra-toggle">
        <span className="text-sm font-medium text-white">Edit school & infrastructure profile</span>
        <span className="ml-auto text-xs text-zinc-500">{f.udise_code ? `UDISE ${f.udise_code}` : "UDISE code not set"}</span>
        <CaretDown size={16} className={`text-zinc-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (loading ? <Loader /> : (
        <form onSubmit={save} className="grid grid-cols-1 gap-4 border-t border-white/[0.07] p-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="udise-infra-form">
          {INFRA.map(([k, l]) => (
            <Field key={k} label={l}>
              {YESNO.has(k) ? (
                <select className="field" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`udise-${k}-input`}>
                  <option value="">—</option><option>Yes</option><option>No</option>
                </select>
              ) : <input className="field" type={NUMS.has(k) ? "number" : "text"} min={0} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`udise-${k}-input`} />}
            </Field>
          ))}
          <button className="btn-primary justify-self-start sm:col-span-2 lg:col-span-4" data-testid="udise-infra-save"><FloppyDisk size={16} /> Save profile</button>
        </form>
      ))}
    </div>
  );
}

const Tile = ({ label, value, tone = "text-white", testid }) => (
  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-4" data-testid={testid}>
    <div className="eyebrow !text-[10px]">{label}</div>
    <div className={`mt-1.5 font-display text-2xl font-semibold ${tone}`} data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
  </div>
);

function StaffSummary({ s, exp }) {
  const groups = [["Gender", s.gender], ["Social category", s.category], ["Nature of appointment", s.appointment], ["Teacher type", s.teacher_type]];
  return (
    <ReportSection icon={UsersThree} title="Teachers & staff" testid="udise-staff-section"
      subtitle={<span data-testid="udise-staff-total">{`${s.teachers} teachers · ${s.total} staff in total`}</span>}
      actions={<><SectionExport type="teacher_summary" exp={exp} /><button className="btn-ghost !py-1.5 !text-xs" onClick={() => exp("xlsx", "staff")} data-testid="udise-section-staff-xlsx"><DownloadSimple size={14} /> Staff list</button></>}>
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label="Total teachers" value={s.teachers} testid="udise-teacher-total-teachers" />
        <Tile label="Trained" value={s.trained} tone="text-emerald-300" testid="udise-teacher-trained" />
        <Tile label="Untrained" value={s.untrained} tone="text-rose-300" testid="udise-teacher-untrained" />
        <Tile label="Female teachers" value={s.gender.find((g) => g.label === "Female")?.total || 0} tone="text-blue-300" testid="udise-teacher-female-teachers" />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {groups.map(([g, items]) => (
          <div key={g} className="rounded-2xl border border-white/[0.07] bg-black/20 p-4">
            <div className="eyebrow !text-[10px] mb-3">{g}</div>
            {items.filter((x) => x.total > 0).map((x) => (
              <div key={x.label} className="flex justify-between border-b border-white/[0.04] py-1.5 text-sm last:border-0"><span className="text-zinc-300">{x.label}</span><span className="font-semibold tabular-nums text-white">{x.total}</span></div>
            ))}
          </div>
        ))}
        <div className="rounded-2xl border border-white/[0.07] bg-black/20 p-4" data-testid="udise-subject-availability">
          <div className="eyebrow !text-[10px] mb-3">Subject-wise teachers</div>
          {s.subjects.map((x) => (
            <div key={x.subject} className="flex justify-between border-b border-white/[0.04] py-1.5 text-sm last:border-0"><span className="text-zinc-300">{x.subject}</span>
              <span className={`font-semibold tabular-nums ${x.teachers ? "text-white" : "text-rose-300"}`}>{x.teachers || "None"}</span></div>
          ))}
        </div>
      </div>
      <p className="mt-4 text-xs text-zinc-500">Trained = a professional qualification (B.Ed, D.El.Ed etc.) is recorded in Staff.</p>
    </ReportSection>
  );
}

const InfraSection = ({ inf, exp, onSaved }) => (
  <ReportSection icon={Buildings} tone="text-orange-300" title="Infrastructure & facilities" subtitle="As recorded in the school profile below." testid="udise-infra-section"
    actions={<SectionExport type="profile" exp={exp} />}>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8" data-testid="udise-infra-summary">
      {INFRA_SUMMARY.map(([k, l]) => (
        <div key={k} className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-4">
          <div className="eyebrow !text-[10px]">{l}</div>
          <div className={`mt-1.5 font-display text-xl font-semibold ${inf[k] === "Yes" ? "text-emerald-300" : inf[k] === "No" ? "text-rose-300" : "text-white"}`} data-testid={`udise-infra-${k}`}>{inf[k] || "—"}</div>
        </div>
      ))}
    </div>
    <Infrastructure onSaved={onSaved} />
  </ReportSection>
);

function GenderBar({ t }) {
  const tot = t.total || 1;
  const parts = [["Boys", t.boys, "bg-blue-400"], ["Girls", t.girls, "bg-rose-400"], ["Transgender", t.transgender, "bg-orange-400"]];
  return (
    <div className="mt-6" data-testid="udise-gender-bar">
      <div className="flex h-2.5 overflow-hidden rounded-full bg-white/5">
        {parts.map(([l, v, c]) => <div key={l} className={`${c} transition-[width] duration-700`} style={{ width: `${(v / tot) * 100}%` }} />)}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400">
        {parts.map(([l, v, c]) => <span key={l} className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${c}`} />{l} <b className="tabular-nums text-zinc-200">{v ?? 0}</b> ({Math.round(((v || 0) / tot) * 100)}%)</span>)}
      </div>
    </div>
  );
}

function ReadinessList({ items, labels }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 8);
  return (
    <>
      <DataTable testid="udise-readiness-table" minWidth={720} head={["Student", "Admission no", "Class", "Missing / invalid", ""]}
        rows={shown.map((s) => [s.name, s.admission_no, `${s.class_name} ${s.section}`,
          <div className="flex flex-wrap gap-1.5">{s.missing.map((k) => <span key={k} className="rounded-full border border-amber-400/30 bg-amber-500/10 px-2.5 py-0.5 text-[11px] text-amber-200">{labels[k]}</span>)}</div>,
          <Link to={`/students?q=${encodeURIComponent(s.admission_no)}`} className="inline-flex items-center gap-1 text-xs font-medium text-blue-300 hover:text-blue-200" data-testid={`udise-readiness-fix-${s.admission_no}`}>Fix <ArrowRight size={12} /></Link>])} />
      {items.length > 8 && (
        <button className="btn-ghost mt-4 !py-1.5 !text-xs" onClick={() => setAll(!all)} data-testid="udise-readiness-toggle">{all ? "Show fewer" : `Show all ${items.length} students`}</button>
      )}
    </>
  );
}

function Readiness({ f, exp }) {
  const { data, loading } = useApi(`/udise/readiness?${qs(f)}`);
  const [field, setField] = useState("");
  if (loading || !data) return <Loader />;
  const pct = data.total ? Math.round((data.ready / data.total) * 100) : 100;
  const labels = Object.fromEntries(data.fields.map((x) => [x.key, x.label]));
  const items = field ? data.students.filter((s) => s.missing.includes(field)) : data.students;
  const fixSheet = async () => {
    try {
      await download(`/student-import/template?${qs({ prefill: "missing", class_id: f.class_id })}`, "udise_fix_up_sheet.xlsx");
      toast.success("Fix-up sheet downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <ReportSection icon={ListChecks} tone="text-amber-300" title="UDISE readiness check" testid="udise-readiness-section"
      subtitle="Students missing PEN, parents' names, category or other UDISE+ essentials. Fix these before you export."
      actions={<><SectionExport type="readiness" exp={exp} /><button className="btn-ghost !py-1.5 !text-xs" onClick={fixSheet} data-testid="udise-fixup-sheet-btn"><DownloadSimple size={14} /> Fix-up sheet</button></>}>
      <div className="mb-6 grid grid-cols-1 gap-5 lg:grid-cols-[260px_1fr]">
        <div className="rounded-2xl border border-white/[0.07] bg-black/20 p-5" data-testid="udise-readiness-score">
          <div className="eyebrow !text-[10px]">Ready for UDISE+</div>
          <div className={`mt-2 font-display text-4xl font-semibold tabular-nums ${pct === 100 ? "text-emerald-300" : pct >= 80 ? "text-amber-200" : "text-rose-300"}`} data-testid="udise-readiness-pct">{pct}%</div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-emerald-400 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
          <p className="mt-3 text-xs text-zinc-400" data-testid="udise-readiness-count">{`${data.ready} of ${data.total} students complete · ${data.students.length} need attention`}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
          {data.fields.map((x) => (
            <button key={x.key} disabled={!x.missing} onClick={() => setField(field === x.key ? "" : x.key)} data-testid={`udise-readiness-field-${x.key}`}
              className={`rounded-2xl border p-3 text-left transition-colors duration-200 disabled:cursor-default ${field === x.key ? "border-amber-400/50 bg-amber-500/10" : "border-white/[0.07] bg-white/[0.03] enabled:hover:bg-white/[0.06]"}`}>
              <div className="text-[11px] text-zinc-400">{x.label}</div>
              <div className={`mt-1 font-display text-xl font-semibold tabular-nums ${x.missing ? "text-amber-200" : "text-emerald-300"}`}>{x.missing || <CheckCircle size={20} weight="fill" />}</div>
            </button>
          ))}
        </div>
      </div>
      {!data.students.length ? (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 text-sm text-emerald-200" data-testid="udise-readiness-ok">
          <CheckCircle size={20} weight="fill" /> All students have the essential UDISE+ details.</div>
      ) : <ReadinessList key={field} items={items} labels={labels} />}
      <p className="mt-4 text-xs text-zinc-500">Tip: download the fix-up sheet, fill in the blanks in Excel, then upload it from <Link to="/students" className="inline-flex items-center gap-1 text-blue-300"><UploadSimple size={12} /> Students → Bulk import</Link>.</p>
    </ReportSection>
  );
}

function TopBar({ f, setF, data, years, classes, exp }) {
  const [csvType, setCsvType] = useState("students");
  const sections = [...new Set((classes || []).map((c) => c.section))];
  return (
    <div className="glass relative overflow-hidden rounded-3xl p-5 sm:p-7" data-testid="udise-topbar">
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/15 blur-3xl" />
      <div className="relative flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="eyebrow mb-2 text-blue-300">UDISE+ data return</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">Academic year {data?.academic_year || "—"}</h2>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-zinc-300" data-testid="udise-code-chip">UDISE code <b className="ml-1 tabular-nums text-white">{data?.udise_code || "Not set"}</b></span>
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-zinc-300">DD/MM/YYYY dates · age as on 30 Sept</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <button className="btn-primary" onClick={() => exp("xlsx", "all")} data-testid="udise-export-xlsx-btn"><FileXls size={18} weight="bold" /> Download Excel (all sheets)</button>
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <select className="bg-transparent pl-3 pr-1 py-1.5 text-sm text-zinc-200 outline-none" value={csvType} onChange={(e) => setCsvType(e.target.value)} data-testid="udise-csv-type-select">
              {CSV_TYPES.map(([v, l]) => <option key={v} value={v} className="bg-[#0c0c16]">{l}</option>)}
            </select>
            <button className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-white/20" onClick={() => exp("csv", csvType)} data-testid="udise-export-csv-btn">
              <FileCsv size={16} weight="bold" /> Download CSV</button>
          </div>
        </div>
      </div>
      <div className="relative mt-6 grid grid-cols-1 gap-3 border-t border-white/[0.07] pt-5 sm:grid-cols-3">
        <Field label="Academic year">
          <select className="field" value={f.academic_year || data?.academic_year || ""} onChange={(e) => setF({ ...f, academic_year: e.target.value })} data-testid="udise-year-select">
            {(years || []).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </Field>
        <Field label="Class">
          <select className="field" value={f.class_id} onChange={(e) => setF({ ...f, class_id: e.target.value })} data-testid="udise-class-select">
            <option value="">All classes</option>
            {classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
          </select>
        </Field>
        <Field label="Section">
          <select className="field" value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })} data-testid="udise-section-select">
            <option value="">All sections</option>
            {sections.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}

function EnrolmentTable({ data, t }) {
  const head = ["Class", "Sec", "Boys", "Girls", "Trans.", "Total", ...CATS.flatMap(() => ["B", "G"]), "Minority", "New", "Rep.", "CWSN", "RTE", "BPL"];
  const line = (r) => [r.class_name, r.section, r.boys, r.girls, r.transgender, r.total, ...CATS.flatMap((c) => [r[`${c}_boys`], r[`${c}_girls`]]), r.minority, r.new_admissions, r.repeaters, r.cwsn, r.rte, r.bpl];
  return (
    <DataTable testid="udise-class-table" minWidth={1100} head={head} rows={data.rows.map(line)} total={line({ ...t, class_name: "Total", section: "" })}
      groups={[["", 2], ["Enrolment", 4], ...CATS.map((c) => [c, 2]), ["Other indicators", 6]]} />
  );
}

export function UdiseReport() {
  const { data: years } = useApi("/udise/years");
  const { data: classes } = useApi("/classes");
  const [f, setF] = useState({ academic_year: "", class_id: "", section: "" });
  const { data, loading, reload } = useApi(`/udise/report?${qs(f)}`);
  const exp = useExporter(f, data?.academic_year);
  const t = data?.totals || {};
  return (
    <div className="space-y-8">
      <TopBar f={f} setF={setF} data={data} years={years} classes={classes} exp={exp} />
      {loading || !data ? <Loader /> : (
        <>
          <Readiness f={f} exp={exp} />
          <div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard label="Total enrolment" value={t.total ?? 0} icon={UsersFour} testid="udise-total" />
              <StatCard label="Boys" value={t.boys ?? 0} tone="blue" testid="udise-boys" delay={0.03} />
              <StatCard label="Girls" value={t.girls ?? 0} tone="rose" testid="udise-girls" delay={0.06} />
              <StatCard label="Transgender" value={t.transgender ?? 0} tone="orange" testid="udise-transgender" delay={0.09} />
              <StatCard label="Minority" value={data.minority_total.total} tone="green" icon={HandHeart} testid="udise-minority" delay={0.12} />
              <StatCard label="CWSN" value={t.cwsn ?? 0} tone="green" icon={Wheelchair} testid="udise-cwsn" delay={0.15} />
              <StatCard label="New admissions" value={t.new_admissions ?? 0} tone="blue" icon={UserPlus} testid="udise-new" delay={0.18} hint={`${t.existing ?? 0} existing students`} />
              <StatCard label="Repeaters" value={t.repeaters ?? 0} tone="orange" testid="udise-repeaters" delay={0.21} hint={`RTE/EWS ${t.rte ?? 0} · BPL ${t.bpl ?? 0}`} />
            </div>
            <GenderBar t={t} />
          </div>

          <ReportSection icon={ChartBar} title="Class-wise enrolment" subtitle="Gender, social category (B = boys, G = girls) and key indicators for every class." testid="udise-enrolment-section"
            actions={<SectionExport type="enrolment" exp={exp} />}>
            {!data.rows.length ? <Empty /> : <EnrolmentTable data={data} t={t} />}
          </ReportSection>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ReportSection title="By social category" subtitle="General, SC, ST and OBC split." actions={<SectionExport type="category" exp={exp} />}>
              <DataTable testid="udise-category-table" head={["Category", "Boys", "Girls", "Total"]}
                rows={data.categories.filter((r) => r.total || r.category !== "Not specified").map((r) => [r.category, r.boys, r.girls, r.total])}
                total={["Total", t.boys ?? 0, t.girls ?? 0, t.total ?? 0]} />
            </ReportSection>
            <ReportSection title="By age" subtitle="Completed years as on 30 September." actions={<SectionExport type="age" exp={exp} />}>
              <DataTable testid="udise-age-table" head={["Age", "Boys", "Girls", "Total"]} rows={data.ages.filter((r) => r.total).map((r) => [r.age, r.boys, r.girls, r.total])} />
            </ReportSection>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ReportSection icon={HandHeart} tone="text-emerald-300" title="Minority & religion" subtitle="Minority = any religion other than Hindu." actions={<SectionExport type="minority" exp={exp} />}>
              <DataTable testid="udise-minority-table" head={["Religion", "Boys", "Girls", "Trans.", "Total"]}
                rows={data.minority.map((r) => bgRow(r.religion, r))} total={bgRow("Total minority", data.minority_total)} />
              <div className="mt-4"><DataTable testid="udise-religion-table" head={["All religions", "Students"]} rows={data.religions.map((r) => [r.religion, r.total])} /></div>
            </ReportSection>
            <ReportSection icon={UserPlus} title="Admissions & repeaters" subtitle={`New = admitted during AY ${data.academic_year}.`} actions={<SectionExport type="admissions" exp={exp} />}>
              <DataTable testid="udise-admissions-table" head={["Type", "Boys", "Girls", "Trans.", "Total"]}
                rows={[bgRow("New admissions", data.admissions.new), bgRow("Existing students", data.admissions.existing), bgRow("Repeaters", data.admissions.repeaters)]} />
            </ReportSection>
          </div>

          <ReportSection icon={Wheelchair} tone="text-emerald-300" title="Children with special needs (CWSN)" subtitle="By type of disability and by class." testid="udise-cwsn-section"
            actions={<SectionExport type="cwsn" exp={exp} />}>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <DataTable testid="udise-cwsn-type-table" head={["Disability", "Boys", "Girls", "Trans.", "Total"]}
                rows={data.cwsn.by_type.map((r) => bgRow(r.type, r))} total={bgRow("Total CWSN", data.cwsn.total)} />
              <DataTable testid="udise-cwsn-class-table" head={["Class", "Section", "Boys", "Girls", "Total"]}
                rows={data.rows.map((r) => [r.class_name, r.section, r.cwsn_boys, r.cwsn_girls, r.cwsn])} total={["Total", "", t.cwsn_boys ?? 0, t.cwsn_girls ?? 0, t.cwsn ?? 0]} />
            </div>
          </ReportSection>

          <ReportSection icon={IdentificationCard} title="Student details (SDMS)" subtitle="One row per student with all UDISE+ profile fields — ready for bulk upload."
            actions={<SectionExport type="students" exp={exp} />}>
            <p className="text-sm text-zinc-400">{`${t.total ?? 0} student records for the selected filters. Mobile numbers are exported as plain 10-digit numbers.`}</p>
          </ReportSection>

          <StaffSummary s={data.staff} exp={exp} />
          <InfraSection inf={data.infrastructure} exp={exp} onSaved={reload} />
        </>
      )}
    </div>
  );
}
